import { useCallback, useEffect, useState } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  config as parallaxConfig,
  IconClose,
  InformationalScreen,
  useTheme,
} from '@adc/parallax-component-library';
import axios from 'axios';
import { MainContainer, TopHeaderBackArrow } from 'Components/utility';
import { type RootState } from 'Reducers/index';
import styled, { css } from 'styled-components';
import { getDocumentPathFromLanguage } from 'Utilities/dataHelpers';
import i18n from 'Utilities/i18n';
import log from 'Utilities/log';
import mediator from 'Utilities/mediator';
import { notifyApp } from 'Utilities/appCommunicator';

const addPxInFontSize = (str: number) => `${str}px`;

const mapStateToProps = ({ env: { phoneLanguage }, nav: { query } }: RootState) => ({
  phoneLanguage,
  isAccountProfile: query.accountProfile === 'true',
});

const connector = connect(mapStateToProps);

type NavigationProps = {
  type: 'pnlibre' | 'toulibre';
};

type Props = ConnectedProps<typeof connector> & NavigationProps;

type DocumentViewProps = {
  textTitle: string;
  text100: string;
  neutralBackground: string;
  fontSizeBodyBaseDefault: string;
  fontSizeHeadlineBaseStrong: string;
};

const DocumentView = styled.div<DocumentViewProps>`
  ${(props) => css`
    background-color: ${props.neutralBackground};

    margin-top: -16px;

    h1 {
      color: ${props.textTitle};
      margin: 0;
      font-size: ${props.fontSizeHeadlineBaseStrong};
      font-family: 'BrandonText';
    }
    h2 {
      color: ${props.textTitle};
      font-family: 'BrandonText';
      font-size: ${props.fontSizeBodyBaseDefault};
    }
    h3 {
      color: ${props.textTitle};
    }
    p {
      color: ${props.text100};
      font-size: ${props.fontSizeBodyBaseDefault};
    }
    table {
      color: ${props.text100};
      text-align: left;
      border-collapse: collapse;
    }
    li {
      color: ${props.text100};
      font-size: ${props.fontSizeBodyBaseDefault};
    }
    a:link {
      color: ${props.textTitle};
    }
    a:visited {
      color: ${props.textTitle};
    }
    strong {
      color: ${props.text100};
      font-size: ${props.fontSizeBodyBaseDefault};
    }
    table,
    th,
    td {
      border: 1px solid ${props.text100};
    }
    th,
    td {
      padding: 8px;
    }

    .accordion .item input[type='checkbox'] {
      position: absolute;
      opacity: 0;
      z-index: -1;
    }

    .accordion .item input:target ~ .collapsible,
    .accordion .item input:checked ~ .collapsible {
      display: block;
    }

    .accordion .item input ~ .collapsible {
      display: none;
    }

    .accordion .item h3 {
      cursor: pointer;
    }
  `}
`;

const documents = {
  toulibre: {
    default: '/files/documents/en-US/toulibre.html',
  },
  pnlibre: {
    default: '/files/documents/en-US/pnlibre.html',
  },
} as const;

const Document = ({ type, phoneLanguage, isAccountProfile }: Props) => {
  const [documentContentHtml, setDocumentContentHtml] = useState('');
  const theme = useTheme();

  const textTitle = theme['text.100'].get();
  const text100 = theme['text.100'].get();
  const neutralBackground = theme['surface.background'].get();
  const fontSizeBodyBaseDefault = parallaxConfig.fonts.body.size['bodyBase.default'];
  const fontSizeHeadlineBaseStrong = parallaxConfig.fonts.body.size['h2.strong'];

  useEffect(() => {
    const mountDocument = async () => {
      try {
        const documentPath = `/assets${getDocumentPathFromLanguage(
          documents[type],
          phoneLanguage
        )}`;

        const { data: documentHtml } = await axios.get<string>(documentPath);

        const bodyElement = /<body.*?>([\s\S]*)<\/body>/.exec(documentHtml);

        if (bodyElement?.length) {
          const docBodyElement = bodyElement[0];

          setDocumentContentHtml(docBodyElement.concat('</br>'));
        }
      } catch (error) {
        log.error(error);
      }
    };

    mountDocument();
  }, [phoneLanguage, type]);

  const onNavButtonPressed = useCallback(() => {
    mediator.publish('router:back');
    notifyApp('adc-webview:navigate-back-app');
  }, []);

  const title =
    type === 'toulibre'
      ? i18n.t('Global.microcopy.common.termsOfUse')
      : i18n.t('Global.microcopy.common.privacyNotice');

  return (
    <MainContainer>
      <InformationalScreen
        topNavigationProps={
          !isAccountProfile
            ? {
                animatedTitle: true,
                title,
                buttonStart: {
                  element: <TopHeaderBackArrow testID="Document.go-back" />,
                  label: 'Go Back',
                  onPress: onNavButtonPressed,
                },
                testID: 'Document.actionTitle',
              }
            : {
                title,
                buttonStart: {
                  element: <IconClose testID="Document.close" />,
                  label: i18n.t('Document.close'),
                  onPress: onNavButtonPressed,
                },
                testID: 'Document.title',
              }
        }
      >
        <InformationalScreen.Page>
          <InformationalScreen.Content>
            <DocumentView
              fontSizeBodyBaseDefault={addPxInFontSize(fontSizeBodyBaseDefault)}
              fontSizeHeadlineBaseStrong={addPxInFontSize(fontSizeHeadlineBaseStrong)}
              text100={text100}
              textTitle={textTitle}
              neutralBackground={neutralBackground}
              dangerouslySetInnerHTML={{ __html: documentContentHtml }}
            />
          </InformationalScreen.Content>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(Document);
